/**
 * The right-hand rail — 280px, on every screen except Account & settings.
 *
 * The handoff specifies this and the first build omitted it. It carries three
 * things, in the order they matter to someone opening the page:
 *
 *  1 · WHERE THE CASE IS, as a sequence of stages. Matt's addition, and the
 *      thing the design did not have: the readiness percentage answers "how
 *      much is done" but nothing answered "what stage is this case at". Those
 *      are different questions, and the second is the one someone asks when
 *      they have been away for a fortnight.
 *  2 · WHO IS ADVISING, which the design put only in Account & settings, three
 *      clicks away from anywhere it would reassure anyone.
 *  3 · The activity feed from the handoff.
 *
 * THE STAGES ARE DERIVED WHERE THEY CAN BE. "Free check done" and "Account
 * created" are facts we hold; "Collecting evidence" reads the determination.
 * The rest — an assigned adviser, a review, a submission — describe product
 * that does not exist, so they render as example content under the same marker
 * as everything else. A stage list that quietly claimed an adviser had been
 * assigned would be the most misleading thing in the whole app.
 */
import { el } from "./dom.js";
import { SAMPLE } from "./sample.js";
import type { Workspace } from "./model.js";

export type StageState = "done" | "current" | "later";

export interface Stage {
  title: string;
  note: string;
  state: StageState;
  /** True where the state is real rather than illustrative. */
  real: boolean;
}

/**
 * The arc of a case, from the free check to a decision.
 *
 * Deliberately not the same list as Submission day's six steps: those are the
 * order of operations on one afternoon, this is the shape of several months.
 * Conflating them would make the rail useless on every day but the last.
 */
export function stagesFor(workspace: Workspace | null): Stage[] {
  const hasCase = workspace !== null;
  const outstanding = workspace?.outstanding ?? 0;
  const gathering = hasCase && outstanding > 0;
  const requirementsDone = hasCase && outstanding === 0;

  return [
    {
      title: "Requirements checked",
      note: hasCase
        ? "Your answers have been run against the rules."
        : "Run the free check to start a case.",
      state: hasCase ? "done" : "current",
      real: true,
    },
    {
      title: "Account created",
      note: "Your case is saved and you can come back to it.",
      state: hasCase ? "done" : "later",
      real: true,
    },
    {
      title: "Collecting what you need",
      note: gathering
        ? `${outstanding} requirement${outstanding === 1 ? "" : "s"} still open.`
        : requirementsDone
          ? "Everything we can check is met on your answers."
          : "The evidence each requirement asks for.",
      state: gathering ? "current" : requirementsDone ? "done" : "later",
      real: true,
    },
    {
      title: "Adviser assigned",
      note: "A registered adviser takes the case on and reviews what you have.",
      state: requirementsDone ? "current" : "later",
      real: false,
    },
    {
      title: "Adviser review",
      note: "The evidence is read before anything is submitted.",
      state: "later",
      real: false,
    },
    {
      title: "Application submitted",
      note: "Fees paid, biometrics booked, documents handed in.",
      state: "later",
      real: false,
    },
    {
      title: "Waiting on a decision",
      note: "Most of this period is silence, and that is normal.",
      state: "later",
      real: false,
    },
  ];
}

function stageList(workspace: Workspace | null): HTMLElement {
  const section = el("section", "rail-block");
  const head = el("div", "rail-head");
  head.append(el("h2", "rail-title", "Where the case is"));
  section.append(head);

  const list = el("ol", "stages");
  for (const stage of stagesFor(workspace)) {
    const li = el("li", `stage stage--${stage.state}`);
    const marker = el("span", "stage-mark", stage.state === "done" ? "✓" : "");
    li.append(marker);
    const body = el("div", "stage-body");
    const title = el("div", "stage-title");
    title.append(el("span", undefined, stage.title));
    // A stage nobody can reach yet must say so where it is read, not only in
    // the banner at the top of the page.
    if (!stage.real) title.append(el("span", "stage-tag", "example"));
    body.append(title);
    body.append(el("div", "stage-note", stage.note));
    li.append(body);
    list.append(li);
  }
  section.append(list);
  return section;
}

function adviserCard(): HTMLElement {
  const section = el("section", "rail-block");
  const head = el("div", "rail-head");
  head.append(el("h2", "rail-title", "Your adviser"));
  head.append(el("span", "rail-tag", "example"));
  section.append(head);

  const card = el("div", "adviser");
  const top = el("div", "adviser-top");
  top.append(el("span", "avatar avatar--sm", SAMPLE.adviser.initials));
  const who = el("div");
  who.append(el("div", "adviser-name", SAMPLE.adviser.name));
  who.append(el("div", "adviser-level", SAMPLE.adviser.level));
  top.append(who);
  card.append(top);

  card.append(el("p", "adviser-note", SAMPLE.adviser.note));

  const meta = el("dl", "adviser-meta");
  for (const [k, v] of [
    ["Registration", SAMPLE.adviser.registration],
    ["Assigned", SAMPLE.adviser.assigned],
  ] as [string, string][]) {
    meta.append(el("dt", undefined, k));
    meta.append(el("dd", undefined, v));
  }
  card.append(meta);

  const message = el("button", "btn", "Message your adviser");
  message.type = "button";
  message.disabled = true;
  card.append(message);

  // The IAA keeps a public register, and pointing at it is the difference
  // between a name on a page and a checkable claim. Cheap, and exactly what
  // someone worried about who is handling their case wants.
  const check = el("p", "adviser-check");
  const link = el("a", undefined, "Check any adviser on the IAA register");
  link.href = "https://www.gov.uk/find-an-immigration-adviser";
  link.rel = "noopener";
  check.append(link);
  card.append(check);

  section.append(card);
  return section;
}

function activityFeed(): HTMLElement {
  const section = el("section", "rail-block");
  const head = el("div", "rail-head");
  head.append(el("h2", "rail-title", "Recent activity"));
  head.append(el("span", "rail-tag", "example"));
  section.append(head);

  const list = el("ul", "activity");
  for (const entry of SAMPLE.activity) {
    const li = el("li");
    li.append(el("span", `avatar avatar--xs avatar--${entry.kind}`, entry.who.slice(0, 2).toUpperCase()));
    const body = el("div");
    body.append(el("div", "activity-what", entry.what));
    body.append(el("div", "activity-when", entry.when));
    li.append(body);
    list.append(li);
  }
  section.append(list);
  return section;
}

export function renderRail(workspace: Workspace | null): HTMLElement {
  const rail = el("aside", "rail");
  rail.setAttribute("aria-label", "Case status");
  rail.append(stageList(workspace));
  rail.append(adviserCard());
  rail.append(activityFeed());
  return rail;
}
